/**
 * Session Warning Modal
 *
 * Displayed when the user's session is about to expire. Shows a live countdown
 * and provides "Extend Session" and "Log Out" buttons.
 *
 * z-index hierarchy:
 *   - SessionWarningModal: 9999 (this file)
 *   - SessionExpiredModal: 10000 (SessionExpiredModal.tsx)
 *   - ConfirmDialog: 10000 (ConfirmDialog.tsx)
 * If the session expires while this modal is showing, SessionExpiredModal's
 * higher z-index ensures it takes visual precedence.
 */

import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

// ─── Styled components (matches SessionExpiredModal pattern) ───

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999; /* Below SessionExpiredModal (10000) — expired modal wins */
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 480px;
  width: 90%;
  padding: 32px;
  text-align: center;
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
  font-size: 20px;
  color: #1a1a1a;
`;

const Message = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const Countdown = styled.span`
  font-weight: 600;
  color: #1a1a1a;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const PrimaryButton = styled.button`
  background-color: #0047ab;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #003580;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: #0047ab;
  border: 1px solid #0047ab;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #f0f4ff;
  }
`;

const ErrorText = styled.p`
  margin: 12px 0 0 0;
  font-size: 13px;
  color: #d32f2f;
  line-height: 1.4;
`;

// ─── Component ───

interface SessionWarningModalProps {
  /** Millisecond timestamp when the session expires */
  expiresAt: number;
  /** Whether an extend-session request is in flight */
  isExtending: boolean;
  /** Error message from a failed extension attempt, or null */
  extensionError: string | null;
  /** Called when user clicks "Extend Session" */
  onExtend: () => void;
  /** Called when user clicks "Log Out" */
  onLogout: () => void;
}

/**
 * Format seconds as "M:SS" for the countdown display.
 */
function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  expiresAt,
  isExtending,
  extensionError,
  onExtend,
  onLogout,
}) => {
  const primaryRef = useRef<HTMLButtonElement>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  // Auto-focus the primary action button for accessibility
  useEffect(() => {
    primaryRef.current?.focus();
  }, []);

  // Live countdown — uses expiresAt - Date.now() each tick (not a decrementing
  // counter) so it handles browser tab backgrounding correctly.
  useEffect(() => {
    const update = (): void =>
      setSecondsRemaining(
        Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)),
      );
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  return (
    <Overlay
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-warning-title"
    >
      <ModalBox>
        <Title id="session-warning-title">Session Expiring Soon</Title>
        <Message>
          Your session will expire in{" "}
          <Countdown>{formatCountdown(secondsRemaining)}</Countdown>. Would you
          like to extend it?
        </Message>
        <ButtonGroup>
          <PrimaryButton
            ref={primaryRef}
            onClick={onExtend}
            disabled={isExtending}
          >
            {isExtending ? "Extending..." : "Extend Session"}
          </PrimaryButton>
          <SecondaryButton onClick={onLogout}>Log Out</SecondaryButton>
        </ButtonGroup>
        {extensionError && <ErrorText>{extensionError}</ErrorText>}
      </ModalBox>
    </Overlay>
  );
};

export default SessionWarningModal;
